import React from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useParams, useNavigate } from 'react-router-dom';

import { useTimeModal } from '../Components/TimeModal/TimeModalContext';
import { normalizeDate, formatDate, formatCaseStatus } from '../Utils/GlobalUtils';

import FileUploadField from '../Components/FileUploadField';
import InputDebtorSelector from '../Components/InputSpecialFields/InputDebtorSelector';
import InputApplicantSelector from '../Components/InputSpecialFields/InputApplicantSelector';
import InputArbitrationCourtSelector from '../Components/InputSpecialFields/InputArbitrationCourtSelector';
import ExplanationModal from '../Components/ExplanationModal';

import CaseDebtors from '../Components/CaseComponents/CaseDebtors';

import HeadlinePanel from '../Components/HeadlinePanel';
import Loading from '../Components/Loading';

export default function EditCase({ action }) {
    const { id } = useParams();
    const { openTimeModal } = useTimeModal();
    const navigate = useNavigate();

    const docTitles = {
        add: 'Добавление новой карточки',
        change: 'Редактирование карточки',
    };

    document.title = `${docTitles[action] || 'Действия над сведениями о деле'} — Личный кабинет Альянс`;

    const [loading, setLoading] = React.useState(true);
    const [status, setStatus] = React.useState(<a>Сохраните изменения</a>);

    const [isAdmin, setIsAdmin] = React.useState(false);
    const [AMUserID, setAMUserID] = React.useState(null);
    
    React.useEffect(() => {
        const token = localStorage.getItem('token');
    
        try {
            const decodedToken = jwtDecode(token);
            if (decodedToken.role==='АппаратСРО') {
                setIsAdmin(true);
            } else {
                setIsAdmin(false);
                setAMUserID(decodedToken.id);
            }
        } catch (error) {
            console.error('Ошибка при декодировании токена: ', error);
        }
    }, []);

    const [contentForm, setContentForm] = React.useState('selectCategory');

    const [initialFormInfo, setInitialFormInfo] = React.useState({});
    
    const [formInfo, setFormInfo] = React.useState({
        id: '',
        number: '',
        internal_number: '',
        category_id: '',
        initial_procedure_id: '',
        procedure_id: '',
        initial_procedure_name: '',
        procedure_name: '',
        user_id: '',
        initial_court_id: '',
        court_id: '',
        initial_court_name: '',
        court_name: '',
        date_court: '',
        application_status: '',
        date_submission_candidate: '',
        date_am_appointment_r: '',
        date_am_appointment_m: '',
        report_interval: '3',
        date_consent_deadline: '',
        is_consent_deadline: 0,
        debtor_id: '',
        debtor_type: 'Физическое лицо',
        debtor_name: '',
        
        applicant_id: '',
        applicant_name: '',
        date_start: '',
        initial_status: '',
        status: 'Заявка',

        comment_explanation: '',

        comment: '',
    });

    React.useEffect(() => {
        if (!isAdmin && action=='add') {
            setFormInfo(prev => ({
                ...prev,
                internal_number: '--',
            }));
        } else {
            setFormInfo(prev => ({
                ...prev,
                internal_number: '',
            }));
        }
    }, [isAdmin, action]);

    React.useEffect(() => {
        if (action=='change') {
            const token = localStorage.getItem('token');
            setStatus(<a><Loading type='inline' /></a>);
            setContentForm('addCaseData');

            axios.get(`/api/get-full-info-case?id=${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            })
            .then((response) => {
                const newData = response.data;

                const updater = (prev) => {
                    const updated = { ...prev };
                    for (const key in newData) {
                        updated[key] = newData[key] ?? '';
                    }
                    return updated;
                };

                setFormInfo(updater);
                setInitialFormInfo(updater);

                setStatus(null);
            })
            .catch((error) => {
                console.error(error);
                setStatus(<a className='mainLabelError'>Не удалось загрузить данные. {error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}</a>);
            })
            .finally(() => {
                setLoading(false);
            })
        } else {
            setLoading(false);
        }
    }, []);

    const [categories, setCategories] = React.useState([]);
    React.useEffect(() => {
        setLoading(true);

        axios.get('/api/categories')
        .then((response) => {
            setCategories(response.data);
        }).catch((error) => {
            console.error(error);
        }).finally(() => {
            setLoading(false);
        })
    }, []);
    const [procedures, setProcedures] = React.useState([]);
    React.useEffect(() => {
        setLoading(true);

        axios.get(`/api/procedures?categoryId=${formInfo.category_id}`)
        .then((response) => {
            setProcedures(response.data);
        }).catch((error) => {
            console.error(error);
        }).finally(() => {
            setLoading(false);
        })
    }, [formInfo.category_id]);

    React.useEffect(() => {
        if (AMUserID) {
            setFormInfo(prev => ({
                ...prev,
                user_id: AMUserID
            }));
        }
    }, [AMUserID]);

    const [courts, setCourts] = React.useState([]);

    React.useEffect(() => {
        axios.get(`/api/get-all-arbitration-courts`)
        .then((response) => {
            setCourts(response.data);
        }).catch((error) => {
            console.error(error);
        })
    }, [])

    React.useEffect(() => {
        if (action !== 'add' || formInfo.court_name) return;

        const timer = setTimeout(() => {
            const number = formInfo.number?.trim();
            if (!number) return;

            const matchedCourt = courts.find(court => 
                court.court_code && number.startsWith(court.court_code)
            );

            if (matchedCourt) {
                setFormInfo(prev => ({
                    ...prev,
                    court_id: matchedCourt.id,
                    court_name: matchedCourt.name,
                }));
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [formInfo.number, courts]);

    const handleChange = (field, value) => (e) => {
        value = value !== undefined ? value : e.target.value;

        setFormInfo((prev) => ({ ...prev, [field]: value }));

        setErrorsForm((prevErrors) => {
            if (!prevErrors[field]) return prevErrors;
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[field];
            return updatedErrors;
        });

        if (field=='debtor_type') {
            setFormInfo((prev) => ({
                ...prev,
                debtor_id: '',
                debtor_name: ''
            }));
        }
    };
    
    const [errorsForm, setErrorsForm] = React.useState({});
    const validateForm = () => {
        const newErrors = {};

        if (!formInfo.internal_number) {
            newErrors.internal_number = 'Обязательное поле';
        }
        if (!formInfo.date_start) {
            newErrors.date_start = 'Обязательное поле';
        }

        if (!formInfo.court_name) {
            newErrors.court = 'Выберите суд';
        }
        if (formInfo.court_name && (formInfo.court_name.length < 2 || formInfo.court_name.length > 150)) {
            newErrors.court = 'Значение не может быть меньше 2 и более 150 символов';
        }
        if (formInfo.internal_number && (formInfo.internal_number.length < 2 || formInfo.internal_number.length > 60)) {
            newErrors.internal_number = 'Значение не может быть меньше 2 и более 60 символов';
        }
        if (formInfo.number && (formInfo.number.length < 2 || formInfo.number.length > 60)) {
            newErrors.number = 'Значение не может быть меньше 2 и более 60 символов';
        }
        if (action=='add' && !formInfo.debtor_name) {
            newErrors.debtor = 'Выберите должника';
        }
        if (action=='add' && !formInfo.debtor_type) {
            newErrors.debtor_type = 'Обязательное поле';
        }

        if (formInfo.date_am_appointment_r && !formInfo.date_am_appointment_m) {
            newErrors.date_am_appointment_m = 'Введите дату утверждения управляющего АС (полная версия)';
        }
        if (formInfo.date_am_appointment_m && !formInfo.date_am_appointment_r) {
            newErrors.date_am_appointment_r = 'Введите дату утверждения управляющего АС (резолютивная часть)';
        }

        if (action=='change' && formInfo.application_status=='Кандидатура утверждена СРО' && !formInfo.date_submission_candidate) {
            newErrors.date_submission_candidate = 'Укажите дату';
        }
        if (action=='change' && formInfo.application_status=='Управляющий утверждён' && !formInfo.date_am_appointment_r) {
            newErrors.date_am_appointment_r = 'Укажите дату';
        }
        if (action=='change' && formInfo.application_status=='Управляющий утверждён' && !formInfo.date_am_appointment_m) {
            newErrors.date_am_appointment_m = 'Укажите дату';
        }

        if (formInfo.status=='В работе' && !formInfo.date_court) {
            newErrors.date_court = 'Введите дату';
        }

        if (formInfo.status=='В работе' && !formInfo.date_am_appointment_r) {
            newErrors.date_am_appointment_r = 'Введите дату утверждения управляющего АС (резолютивная часть)';
        }
        if (formInfo.status=='В работе' && !formInfo.date_am_appointment_m) {
            newErrors.date_am_appointment_m = 'Введите дату утверждения управляющего АС (полная версия)';
        }

        setErrorsForm(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setStatus(<a className='mainLabelError'>Данные не сохранены. Проверьте корректность введенных полей</a>);
            return false;
        } else {
            return true;
        }
    };

    const addNewCase = () => {
        const token = localStorage.getItem('token');


        const formData = new FormData();

        formData.append('category_id', formInfo.category_id ?? '');
        formData.append('procedure_id', formInfo.procedure_id ?? '');
        formData.append('court_id', formInfo.court_id);
        formData.append('court_name', formInfo.court_name);
        formData.append('number', formInfo.number);
        formData.append('case_link', formInfo.case_link ?? '');
        formData.append('internal_number', formInfo.internal_number);
        formData.append('debtor_type', formInfo.debtor_type);
        formData.append('debtor_id', formInfo.debtor_id);
        formData.append('debtor_name', formInfo.debtor_name);
        formData.append('debtor_address', formInfo.debtor_address ?? '');
        formData.append('applicant_id', formInfo.applicant_id);
        formData.append('applicant_name', formInfo.applicant_name);
        formData.append('date_court', formInfo.date_court ?? '');
        formData.append('date_start', formInfo.date_start);
        formData.append('date_am_appointment_r', normalizeDate(formInfo.date_am_appointment_r));
        formData.append('date_am_appointment_m', normalizeDate(formInfo.date_am_appointment_m));
        formData.append('date_procedure_start_r', normalizeDate(formInfo.date_procedure_start_r));
        formData.append('date_procedure_start_m', normalizeDate(formInfo.date_procedure_start_m));
        formData.append('report_interval', formInfo.report_interval ?? '3');
        formData.append('comment', formInfo.comment);
        if (formInfo.user_id) formData.append('user_id', formInfo.user_id);
        formData.append('status', formInfo.status);

        if (files.bid) {
            formData.append('file_bid', files.bid);
        }

        axios.post('/api/add-new-case', formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
            setStatus(<a>Данные сохранены</a>);
            openTimeModal('Карточка добавлена', 'success');
            navigate(`/case/${response.data.caseId}`);
        })
        .catch((error) => {
            console.error(error);
            openTimeModal(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
            setStatus(<a className='mainLabelError'>Данные не сохранены</a>);
        });
    };

    const [circumstance, setCircumstance] = React.useState('');

    const changeCase = () => {
        const token = localStorage.getItem('token');

        const payload = {
            id: formInfo.id,
            category_id: formInfo.category_id,
            procedure_id: formInfo.procedure_id,
            court_id: formInfo.court_id,
            court_name: formInfo.court_name,
            number: formInfo.number,
            case_link: formInfo.case_link,
            internal_number: formInfo.internal_number,
            debtor_type: formInfo.debtor_type,
            debtor_id: formInfo.debtor_id,
            debtor_name: formInfo.debtor_name.trim(),
            debtor_address: formInfo.debtor_address,
            date_mailing: normalizeDate(formInfo.date_mailing),
            date_court: normalizeDate(formInfo.date_court),
            date_start: normalizeDate(formInfo.date_start),
            date_submission_candidate: normalizeDate(formInfo.date_submission_candidate),
            date_procedure_start_r: normalizeDate(formInfo.date_procedure_start_r),
            date_procedure_start_m: normalizeDate(formInfo.date_procedure_start_m),
            date_am_appointment_r: normalizeDate(formInfo.date_am_appointment_r),
            date_am_appointment_m: normalizeDate(formInfo.date_am_appointment_m),
            report_interval: formInfo.report_interval,
            date_consent_deadline: normalizeDate(formInfo.date_consent_deadline),
            is_consent_deadline: formInfo.is_consent_deadline,
            user_id: formInfo.user_id,
            status: formInfo.status,
            new_messages: []
        };

        // условие 1: изменилась процедура
        if (initialFormInfo.procedure_id !== formInfo.procedure_id) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Процедура изменена. ${initialFormInfo.procedure_name} → ${formInfo.procedure_name}`,
            });
        }

        // условие 2: изменился суд
        if (initialFormInfo.court_id !== formInfo.court_id) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Суд изменён. ${initialFormInfo.court_name} → ${formInfo.court_name}`,
            });
        }

        // условие 3: изменился статус
        if (initialFormInfo.status !== formInfo.status) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Статус изменён. ${initialFormInfo.status} → ${formInfo.status}`,
            });
        }

        // условие 4: изменилась дата утверждения управляющего АС r и m
        if (initialFormInfo.date_am_appointment_r !== formInfo.date_am_appointment_r) {
            if (!initialFormInfo.date_am_appointment_r) {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Указана дата утверждения управляющего АС (резолютивная часть). ${formatDate(formInfo.date_am_appointment_r)}`,
                });
            } else {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Дата утверждения управляющего АС изменена (резолютивная часть). ${formatDate(initialFormInfo.date_am_appointment_r)} → ${formatDate(formInfo.date_am_appointment_r)}`,
                });
            }
        }

        if (initialFormInfo.date_am_appointment_m !== formInfo.date_am_appointment_m) {
            if (!initialFormInfo.date_am_appointment_m) {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Указана дата утверждения управляющего АС (полная версия). ${formatDate(formInfo.date_am_appointment_m)}`,
                });
            } else {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Дата утверждения управляющего АС изменена (полная версия). ${formatDate(initialFormInfo.date_am_appointment_m)} → ${formatDate(formInfo.date_am_appointment_m)}`,
                });
            }
        }

        // условие 5: изменилась дата судебного заседания
        if (initialFormInfo.date_court !== formInfo.date_court) {
            if (!initialFormInfo.date_court) {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Указана дата судебного заседания об утверждении управляющего: ${formatDate(formInfo.date_court)}`,
                });
            } else {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Дата судебного заседания об утверждении управляющего изменена. ${formatDate(initialFormInfo.date_court)} → ${formatDate(formInfo.date_court)}`,
                });
            }
        }

        // условие 6: изменилась дата введения процедуры r и m
        if (initialFormInfo.date_procedure_start_r !== formInfo.date_procedure_start_r) {
            if (!initialFormInfo.date_procedure_start_r) {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Указана дата введения процедуры (резолютивная часть): ${formatDate(formInfo.date_procedure_start_r)}`,
                });
            } else {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Дата введения процедуры изменена (резолютивная часть). ${formatDate(initialFormInfo.date_procedure_start_r)} → ${formatDate(formInfo.date_procedure_start_r)}`,
                });
            }
        }

        if (initialFormInfo.date_procedure_start_m !== formInfo.date_procedure_start_m) {
            if (!initialFormInfo.date_procedure_start_m) {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Указана дата введения процедуры (полная версия): ${formatDate(formInfo.date_procedure_start_m)}`,
                });
            } else {
                payload.new_messages.push({
                    type: 'change_data',
                    initiator_id: formInfo.user_id,
                    message: `Дата введения процедуры изменена (полная версия). ${formatDate(initialFormInfo.date_procedure_start_m)} → ${formatDate(formInfo.date_procedure_start_m)}`,
                });
            }
        }

        // условие 7: изменился номер дела
        if (initialFormInfo.number !== formInfo.number) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Номер дела изменён. ${initialFormInfo.number} → ${formInfo.number}`,
            });
        }
        // условие 8: изменился номер входящей корреспонденции
        if (initialFormInfo.internal_number !== formInfo.internal_number) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Номер входящей корреспонденции изменён. ${initialFormInfo.internal_number} → ${formInfo.internal_number}`,
            });
        }

        // условие 9: изменилась конечная дата предоставления согласий
        if (initialFormInfo.date_consent_deadline !== formInfo.date_consent_deadline) {
            payload.new_messages.push({
                type: 'change_data',
                initiator_id: formInfo.user_id,
                message: `Конечная дата предоставления согласий изменена. ${formatDate(initialFormInfo.date_consent_deadline)} → ${formatDate(formInfo.date_consent_deadline)}`,
            });
        }

        axios.put('/api/change-case', payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        .then(() => {
            setStatus(<a>Данные сохранены</a>);
            openTimeModal('Данные изменены', 'success');

            navigate(`/case/${formInfo.id}`);
        })
        .catch((error) => {
            console.error(error);
            openTimeModal(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
            setStatus(<a className='mainLabelError'>Данные не сохранены</a>);
        });
    };

    const handleSave = () => {
        setLoading(true);
        setStatus(<a><Loading type='inline' /></a>);

        if (validateForm()) {
            if (action=='add') addNewCase();
            if (action=='change') changeCase();
        }

        setLoading(false);
    };



    const [files, setFiles] = React.useState({
        bid: null
    });
    const handleFileChange = (field) => (e) => {
        const file = e.target.files[0];
        setFiles((prev) => ({
          ...prev,
          [field]: file || null,
        }));
    };
    
    const handleFileReset = (field) => () => {
        setFiles((prev) => ({
          ...prev,
          [field]: null,
        }));
      };

    const pageLabels = {
        add: 'Добавление входящей корреспонденции',
        change: 'Редактирование сведений о деле',
    };

    const [isExplanationModal, setIsExplanationModal] = React.useState(false);
    const [isOpenStatusCase, setIsOpenStatusCase] = React.useState(false);

    return (
    <>
        {isExplanationModal && <ExplanationModal closeModal={() => setIsExplanationModal(false)} formInfo={formInfo} setFormInfo={setFormInfo} circumstance={circumstance} onSuccess={() => setCircumstance('')} />}

        <div className='mainContainer'>
            <HeadlinePanel 
                label={pageLabels[action] || 'Действия над сведениями о деле'}
                type='Назад' 
            />
            <div className='containerInner'>
                <div className='pageLimiter'>
                    {action==='add' &&
                    <div className='stepsPanel'>
                        {contentForm=='selectCategory' &&
                        <div className='step'>
                            <h3>Выбор типа карточки</h3>
                        </div>
                        }
                        {contentForm=='selectProcedure' &&
                        <>
                            <div className='step'>
                                <h3 onClick={() => {setContentForm('selectCategory')}}>Выбор типа карточки</h3>
                            </div>
                            <div className='step'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="currentColor" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                            </div>
                            <div className='step'>
                                <h3 onClick={() => {setContentForm('selectProcedure')}}>Выбор процедуры</h3>
                            </div>
                        </>
                        }
                        {contentForm=='addCaseData' &&
                        <>
                            <div className='step'>
                                <h3 onClick={() => {setContentForm('selectCategory')}}>Выбор типа карточки</h3>
                            </div>
                            <div className='step'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="currentColor" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                            </div>
                            <div className='step'>
                                <h3 onClick={() => {setContentForm('selectProcedure')}}>Выбор процедуры</h3>
                            </div>
                            <div className='step'>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="currentColor" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                            </div>
                            <div className='step'>
                                <h3>Ввод данных карточки</h3>
                            </div>
                        </>
                        }
                    </div>}
                    {loading ? <Loading /> :
                    <>
                    {contentForm=='selectCategory' &&
                    <>
                        <div className='selectorsWrapper'>
                            {categories.map((category) => (
                                <div className='selector' onClick={(e) => { handleChange('category_id', category.id)(e); setContentForm('selectProcedure') }} key={category.id}>
                                    <label htmlFor={category.id}>{category.name}</label>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="black" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                                </div>
                            ))}
                        </div>
                    </>
                    }
                    {contentForm=='selectProcedure' &&
                    <>
                        <div className='selectorsWrapper'>
                            {procedures.map((procedure) => (
                                <div className='selector' onClick={(e) => { handleChange('procedure_id', procedure.id)(e); handleChange('procedure_name', procedure.name)(e); setContentForm('addCaseData') }} key={procedure.id}>
                                    <label htmlFor={procedure.id}>{procedure.name}</label>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="black" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                                </div>
                            ))}
                        </div>
                        <div className='panelFormConfirmation'>
                            <button type='button' className='button optionalButton' onClick={(e) => { handleChange('procedure_id', null)(e); handleChange('procedure_name', null)(e);  setContentForm('addCaseData') }}>Пропустить</button>
                            {action==='add' && <div>Процедуру можно будет указать позднее</div>}
                        </div>
                    </>
                    }
                    {contentForm=='addCaseData' &&
                    <>
                        <h3 className='formHeadline'>Основная информация</h3>
                        <div className='labelInputWrapper'>
                            <a className='label'>Арбитражный суд*</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <InputArbitrationCourtSelector
                                        initialSearchTerm={formInfo.court_name}
                                        formInfo={formInfo}
                                        setFormInfo={setFormInfo}
                                        errorsForm={errorsForm}
                                        setErrorsForm={setErrorsForm}
                                        isAddNew={true}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className='labelInputWrapper'>
                            <a className='label'>Номер дела</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                        type='text' 
                                        value={formInfo.number} 
                                        onChange={handleChange('number')} 
                                        className={errorsForm.number ? 'inputError' : ''}
                                        style={action=='add' ? {borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px'} : {}}
                                        placeholder='' 
                                        minLength='' 
                                        maxLength='60' 
                                        disabled={!isAdmin && action=='change'}
                                        required 
                                    />
                                    {action=='add' &&
                                    <div
                                        className='inputHelperText'
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M108,84a16,16,0,1,1,16,16A16,16,0,0,1,108,84Zm128,44A108,108,0,1,1,128,20,108.12,108.12,0,0,1,236,128Zm-24,0a84,84,0,1,0-84,84A84.09,84.09,0,0,0,212,128Zm-72,36.68V132a20,20,0,0,0-20-20,12,12,0,0,0-4,23.32V168a20,20,0,0,0,20,20,12,12,0,0,0,4-23.32Z"></path></svg>
                                        <p>Введите номер дела - суд подставится автоматически, если Вы его еще не выбрали</p>
                                    </div>}
                                    {errorsForm.number && <div className='errorUnderInput'>{errorsForm.number}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Ссылка на дело</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                        type='text' 
                                        value={formInfo.case_link} 
                                        onChange={handleChange('case_link')} 
                                        className={errorsForm.case_link ? 'inputError' : ''}
                                        placeholder='' 
                                        minLength='' 
                                        maxLength='400' 
                                        required 
                                    />
                                    {errorsForm.case_link && <div className='errorUnderInput'>{errorsForm.case_link}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Номер входящей корреспонденции*</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                        type='text' 
                                        value={formInfo.internal_number} 
                                        onChange={handleChange('internal_number')} 
                                        className={errorsForm.internal_number ? 'inputError' : ''}
                                        placeholder='' 
                                        minLength='' 
                                        maxLength='60' 
                                        disabled={!isAdmin}
                                        required 
                                    />
                                    {errorsForm.internal_number && <div className='errorUnderInput'>{errorsForm.internal_number}</div>}
                                </div>
                            </div>
                        </div>
                        {action=='add' &&
                        <>
                        <div className='labelInputWrapper'>
                            <a className='label'>Категория должника*</a>
                            <div className='labelInputRight'>
                                <div className='radioSwitchWrapper'>
                                    <label className={`radioSwitchLabel ${formInfo.debtor_type === 'Физическое лицо' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Физическое лицо'
                                            checked={formInfo.debtor_type === 'Физическое лицо'}
                                            onChange={handleChange('debtor_type')}
                                        />
                                        <span>Физическое лицо</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.debtor_type === 'Юридическое лицо' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Юридическое лицо'
                                            checked={formInfo.debtor_type === 'Юридическое лицо'}
                                            onChange={handleChange('debtor_type')}
                                        />
                                        <span>Юридическое лицо</span>
                                    </label>
                                </div>
                                {errorsForm.debtor_type && <div className='errorUnderInput'>{errorsForm.debtor_type}</div>}
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Должник*</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <InputDebtorSelector
                                        initialSearchTerm={formInfo.debtor_name}
                                        formInfo={formInfo}
                                        setFormInfo={setFormInfo}
                                        errorsForm={errorsForm}
                                        setErrorsForm={setErrorsForm}
                                        isAddNew={true}
                                        entity_type={formInfo.debtor_type}
                                    />
                                </div>
                            </div>
                        </div>
                        {!formInfo.debtor_id &&
                        <div className='labelInputWrapper'>
                            <a className='label'>Адрес должника</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                    type='text' 
                                    value={formInfo.debtor_address} 
                                    onChange={handleChange('debtor_address')} 
                                    className={errorsForm.debtor_address ? 'inputError' : ''}
                                    placeholder='Например, Россия, г.Санкт-Петербург, ул.Рубинштейна, 8' 
                                    minLength='' 
                                    maxLength='150' 
                                    disabled={!isAdmin && action=='change'}
                                    required />
                                    {errorsForm.debtor_address && <div className='errorUnderInput'>{errorsForm.debtor_address}</div>}
                                </div>
                            </div>
                        </div>}
                        </>}
                        {action=='add' &&
                        <div className='labelInputWrapper'>
                            <a className='label'>Заявитель</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <InputApplicantSelector
                                        initialSearchTerm={formInfo.applicant_name}
                                        formInfo={formInfo}
                                        setFormInfo={setFormInfo}
                                        errorsForm={errorsForm}
                                        setErrorsForm={setErrorsForm}
                                        isAddNew={true}
                                    />
                                </div>
                            </div>
                        </div>}
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата начала учёта*</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_start?.split('T')[0]}
                                    onChange={handleChange('date_start')} 
                                    className={errorsForm.date_start ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    disabled={!isAdmin && action=='change'}
                                    required />
                                    {errorsForm.date_start && <div className='errorUnderInput'>{errorsForm.date_start}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Статус карточки</a>
                            <div className='labelInputRight'>
                                <div
                                    className='horizontalContainer'
                                >
                                    {!isOpenStatusCase ? 
                                        formatCaseStatus(formInfo.status, 'textPill', '', 'a')
                                        :
                                        <p></p>
                                    }
                                    <button
                                        type='button'
                                        className='miniButton'
                                        style={{width: 'fit-content'}}
                                        onClick={() => setIsOpenStatusCase(!isOpenStatusCase)}
                                    >
                                        {!isOpenStatusCase ?
                                        'Изменить статус'
                                        :
                                        'Свернуть'
                                        }
                                    </button>
                                </div>
                                {isOpenStatusCase &&
                                <div className='radioSwitchWrapper verticalRSWrapper'>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Заявка' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Заявка'
                                            checked={formInfo.status === 'Заявка'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Заявка</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Направлен отказ' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Направлен отказ'
                                            checked={formInfo.status === 'Направлен отказ'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Направлен отказ</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Управляющий не назначен' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий не назначен'
                                            checked={formInfo.status === 'Управляющий не назначен'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий не назначен</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'В работе' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='В работе'
                                            checked={formInfo.status === 'В работе'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>В работе (Управляющий назначен)</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Управляющий отстранен' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий отстранен'
                                            checked={formInfo.status === 'Управляющий отстранен'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий отстранен</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Управляющий освобожден' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Управляющий освобожден'
                                            checked={formInfo.status === 'Управляющий освобожден'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Управляющий освобожден</span>
                                    </label>
                                    <label className={`radioSwitchLabel ${formInfo.status === 'Завершено' ? 'selected' : ''}`}>
                                        <input
                                            type='radio'
                                            name='typeSelector'
                                            value='Завершено'
                                            checked={formInfo.status === 'Завершено'}
                                            onChange={handleChange('status')}
                                        />
                                        <span>Завершено</span>
                                    </label>
                                </div>}
                                {action=='change' && <p className='description'>Статус также изменяется автоматически: при указании даты утверждения арбитражного управляющего АС устанавливается статус «В работе (Управляющий назначен)»</p>}
                                {errorsForm.status && <div className='errorUnderInput'>{errorsForm.status}</div>}
                            </div>
                        </div>

                        <div style={{height: '20px'}}></div>

                        <h3 className='formHeadline'>Заявка</h3>
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата проведения рассылки опроса</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_mailing?.split('T')[0]}
                                    onChange={handleChange('date_mailing')} 
                                    className={errorsForm.date_mailing ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    disabled={true}
                                    required />
                                    {isAdmin && <p className='description'>От данной даты расчитывается конечный срок предоставления арбитражными управляющими согласия на ведение дела (+3 дня)</p>}
                                    {errorsForm.date_mailing && <div className='errorUnderInput'>{errorsForm.date_mailing}</div>}
                                </div>
                            </div>
                        </div>
                        {isAdmin &&
                        <div className='labelInputWrapper'>
                            <a className='label'>Конечная дата предоставления согласий управляющими</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                        type='date' 
                                        value={formInfo.date_consent_deadline?.split('T')[0]}
                                        onChange={handleChange('date_consent_deadline')} 
                                        className={errorsForm.date_consent_deadline ? 'inputError' : ''}
                                        placeholder='' 
                                        minLength='' 
                                        maxLength='' 
                                        disabled={formInfo.user_id || formInfo.mailing_status=='Письма отправлены'}
                                        required 
                                    />
                                    {(formInfo.user_id || formInfo.mailing_status=='Письма отправлены') && <p className='description'>После рассылки опроса или выбора управляющего изменение этого поля недоступно</p>}
                                    {errorsForm.date_consent_deadline && <div className='errorUnderInput'>{errorsForm.date_consent_deadline}</div>}
                                    {formInfo.date_consent_deadline &&
                                    <div className='panel'>
                                        <span className='horizontalContainer'>
                                            <p className='description'>Временно продлить срок предоставлений согласий</p>
                                            <div className='checkboxWrapper'>
                                                <label className='switch' htmlFor={`is_consent_deadline`}>
                                                    <input 
                                                        type='checkbox'
                                                        id={`is_consent_deadline`}
                                                        checked={formInfo.is_consent_deadline == '0'} 
                                                        onChange={(e) =>
                                                            setFormInfo((prev) => ({
                                                                ...prev,
                                                                is_consent_deadline: prev.is_consent_deadline == '0' ? '1' : '0',
                                                            }))
                                                        }
                                                    />
                                                    <div className='slider round'></div>
                                                </label>
                                            </div>
                                        </span>
                                    </div>}
                                </div>
                            </div>
                        </div>}
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата выбора управляющего СРО</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input 
                                        type='date' 
                                        value={formInfo.date_candidate_selection?.split('T')[0]}
                                        onChange={handleChange('date_candidate_selection')} 
                                        className={errorsForm.date_candidate_selection ? 'inputError' : ''}
                                        placeholder='' 
                                        minLength='' 
                                        maxLength='' 
                                        disabled={true}
                                        required 
                                    />
                                    {isAdmin && <p className='description'>Подставляется автоматически</p>}
                                    {errorsForm.date_candidate_selection && <div className='errorUnderInput'>{errorsForm.date_candidate_selection}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата направления кандидатуры в суд</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_submission_candidate?.split('T')[0]}
                                    onChange={handleChange('date_submission_candidate')} 
                                    className={errorsForm.date_submission_candidate ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    disabled={!isAdmin}
                                    required />
                                    {errorsForm.date_submission_candidate && <div className='errorUnderInput'>{errorsForm.date_submission_candidate}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата судебного заседания об утверждении АУ</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_court?.split('T')[0]}
                                    onChange={handleChange('date_court')} 
                                    className={errorsForm.date_court ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    {errorsForm.date_court && <div className='errorUnderInput'>{errorsForm.date_court}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата утверждения управляющего АС</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_am_appointment_m?.split('T')[0]}
                                    onChange={handleChange('date_am_appointment_m')} 
                                    className={errorsForm.date_am_appointment_m ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    <p className='description'>Полная версия</p>
                                    {errorsForm.date_am_appointment_m && <div className='errorUnderInput'>{errorsForm.date_am_appointment_m}</div>}
                                </div>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_am_appointment_r?.split('T')[0]}
                                    onChange={handleChange('date_am_appointment_r')} 
                                    className={errorsForm.date_am_appointment_r ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    <p className='description'>Резолютивная часть</p>
                                    {errorsForm.date_am_appointment_r && <div className='errorUnderInput'>{errorsForm.date_am_appointment_r}</div>}
                                </div>
                            </div>
                        </div>

                        <div style={{height: '20px'}}></div>

                        <h3 className='formHeadline'>Процедура</h3>
                        <div className='labelInputWrapper'>
                            <a className='label'>Процедура</a>
                            <div className='labelInputRight'>
                                <span className='horizontalContainer'>
                                    <a>{formInfo.procedure_name ?? 'Не выбрана'}</a>
                                    <button 
                                        type='button' 
                                        className='miniButton' 
                                        style={{width: 'fit-content'}}
                                        onClick={() => {setContentForm('selectProcedure')}}
                                    >
                                        {formInfo.procedure_name ? 'Изменить процедуру' : 'Выбрать процедуру'}
                                    </button>
                                </span>
                            </div>
                        </div>
                        <div className='labelInputWrapper'>
                            <a className='label'>Дата введения процедуры</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_procedure_start_m?.split('T')[0]}
                                    onChange={handleChange('date_procedure_start_m')} 
                                    className={errorsForm.date_procedure_start_m ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    <p className='description'>Полная версия</p>
                                    {errorsForm.date_procedure_start_m && <div className='errorUnderInput'>{errorsForm.date_procedure_start_m}</div>}
                                </div>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_procedure_start_r?.split('T')[0]}
                                    onChange={handleChange('date_procedure_start_r')} 
                                    className={errorsForm.date_procedure_start_r ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    <p className='description'>Резолютивная часть</p>
                                    {errorsForm.date_procedure_start_r && <div className='errorUnderInput'>{errorsForm.date_procedure_start_r}</div>}
                                </div>
                            </div>
                        </div>
                        {/* {formInfo.status=='В работе' &&
                        <div className='labelInputWrapper'>
                            <a className='label'>Конечный срок предоставления отчёта</a>
                            <div className='labelInputRight'>
                                <div className='inputWrapper'>
                                    <input type='date' 
                                    value={formInfo.date_end?.split('T')[0]}
                                    onChange={handleChange('date_end')} 
                                    className={errorsForm.date_end ? 'inputError' : ''}
                                    placeholder='' 
                                    minLength='' 
                                    maxLength='' 
                                    required />
                                    {errorsForm.date_end && <div className='errorUnderInput'>{errorsForm.date_end}</div>}
                                </div>
                            </div>
                        </div>} */}

                        <div style={{height: '20px'}}></div>

                        {action=='add' &&
                        <>
                            <div className='labelInputWrapper'>
                                <a className='label'>Файл</a>
                                <div className='labelInputRight'>
                                    <div className='inputWrapper'>
                                        <FileUploadField
                                        label='Прикрепить файл'
                                        buttonLabel='Прикрепить файл'
                                        file={files.bid}
                                        onChange={handleFileChange('bid')}
                                        onReset={handleFileReset('bid')}
                                        />
                                        {errorsForm.bid && <div className='errorUnderInput'>{errorsForm.bid}</div>}
                                    </div>
                                </div>
                            </div>

                            <div style={{height: '20px'}}></div>

                            <div className='labelInputWrapper'>
                                <a className='label'>Сообщение</a>
                                <div className='labelInputRight'>
                                    <div className='inputWrapper'>
                                        <div>
                                            <textarea 
                                                cols='10' 
                                                rows='10' 
                                                value={formInfo.comment}  
                                                onChange={handleChange('comment')}
                                                className={errorsForm.comment ? 'inputError' : ''}
                                                minLength='' 
                                                maxLength='900' 
                                                require=''
                                            >
                                            </textarea>
                                            <p className='description'>{formInfo.comment.length}/900</p>
                                        </div>
                                    </div>
                                    {errorsForm.comment && <div className='errorUnderInput'>{errorsForm.comment}</div>}
                                </div>
                            </div>
                        </>}

                        <div className='panelFormConfirmation'>
                            <button type='button' className='button' onClick={handleSave}>Сохранить</button>
                            {status && <div>{status}</div>}
                        </div>
                    </>
                    }
                    </>
                    }
                </div>
            </div>
        </div>
    </>
    )
}
